/**
 * DemoPlayer.ts - Generic Demo Player
 *
 * Replays recorded command sequences from JSON data.
 * Uses setTimeout-based scheduling for accurate timing.
 * Loops automatically when the sequence finishes.
 * Includes idle detection for auto-demo mode.
 */

import type { ICommand } from './Command';
import { Command, CommandExecutor } from './Command';
import type { GameState } from './GameState';
import type { DemoData } from './Recorder';

type StatusListener = (playing: boolean) => void;
type CommandListener<TCmd extends ICommand> = (cmd: TCmd) => void;

export class DemoPlayer<TState extends object, TCmd extends ICommand> {
  private _data: DemoData<TCmd> | null = null;
  private _playing = false;
  private _timers: ReturnType<typeof setTimeout>[] = [];
  private _loopTimer: ReturnType<typeof setTimeout> | null = null;
  private _statusListeners: Set<StatusListener> = new Set();
  private _commandListeners: Set<CommandListener<TCmd>> = new Set();

  constructor(
    private executor: CommandExecutor<TState, TCmd>,
    private gameState: GameState<TState>,
  ) {}

  onStatusChange(cb: StatusListener): () => void {
    this._statusListeners.add(cb);
    return () => this._statusListeners.delete(cb);
  }

  onCommandExecuted(cb: CommandListener<TCmd>): () => void {
    this._commandListeners.add(cb);
    return () => this._commandListeners.delete(cb);
  }

  private _notifyStatus(): void {
    this._statusListeners.forEach((cb) => cb(this._playing));
  }

  /** Load demo data (parsed JSON object) */
  load(jsonData: DemoData<TCmd>): void {
    this._data = jsonData;
    console.log(
      '[DemoPlayer] Loaded demo:',
      jsonData.commands.length,
      'commands,',
      jsonData.duration + 'ms',
    );
  }

  /** Start or restart playback */
  play(): void {
    if (!this._data || this._data.commands.length === 0) {
      console.warn('[DemoPlayer] No demo data loaded');
      return;
    }

    this.stop();
    this._playing = true;
    this._notifyStatus();
    this._showBanner(true);
    this._scheduleCommands();

    console.log('[DemoPlayer] Playback started');
  }

  /** Schedule all commands using setTimeout */
  private _scheduleCommands(): void {
    const commands = this._data!.commands;

    for (const cmd of commands) {
      const timer = setTimeout(() => {
        if (!this._playing) return;

        const command = Command.fromJSON(cmd) as unknown as TCmd;
        const newState = this.executor.execute(command, this.gameState.get());
        this.gameState.set(newState);

        this._commandListeners.forEach((cb) => cb(command));
      }, cmd.timestamp);

      this._timers.push(timer);
    }

    // Schedule loop restart after the last command + a small pause
    const loopDelay = this._data!.duration + 2000;
    this._loopTimer = setTimeout(() => {
      if (!this._playing) return;
      console.log('[DemoPlayer] Looping...');
      this.gameState.reset();
      this._timers = [];
      this._scheduleCommands();
    }, loopDelay);
  }

  /** Stop playback immediately */
  stop(): void {
    for (const timer of this._timers) {
      clearTimeout(timer);
    }
    this._timers = [];

    if (this._loopTimer) {
      clearTimeout(this._loopTimer);
      this._loopTimer = null;
    }

    if (this._playing) {
      this._playing = false;
      this._notifyStatus();
      this._showBanner(false);
      console.log('[DemoPlayer] Playback stopped');
    }
  }

  /** Check if currently playing */
  isPlaying(): boolean {
    return this._playing;
  }

  // =========================================================================
  // UI
  // =========================================================================

  private _showBanner(visible: boolean): void {
    const banner = document.getElementById('demo-banner');
    if (banner) {
      banner.style.display = visible ? 'block' : 'none';
    }
  }
}
